import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { createPortal } from "react-dom";
import ChevronDownIcon from "../../assets/svg/chevron-down-icon";
import { MultiSelectDropdownOption, MultiSelectDropdownProps } from "./types";
import SelectedChips from "./components/SelectedChips";
import SearchInput from "./components/SearchInput";
import OptionsList from "./components/OptionsList";
import CircularLoader from "../Loader/CircularLoader";
import { visibleScrollbar } from "../../utils/functions/get-visible-scrollbar";

const MultiSelectSearchableDropdown: React.FC<MultiSelectDropdownProps> = ({
  options,
  value = [],
  onChange,
  id,
  placeholder = "Select options",
  disabled = false,
  className = "",
  triggerClassName = "",
  dropdownClassName = "",
  optionClassName = "",
  ariaLabel,
  ariaDescribedBy,
  showChevron = true,
  label,
  labelClassName = "",
  searchPlaceholder = "Search options...",
  maxDisplayedSelections = 3,
  showSearch = true,
  enableApiSearch = false,
  onSearch,
  initialOptions,
  searchDebounceMs = 500,
  loadingText = "",
  noResultsText = "No results found",
  placeholderClassName = "",
  dropdownOpenClassName,
  searchInputClassName,
  optionsListClassName,
  searchIconClassName,
  selectedOptionClassName,
  inputContainerClassName,
  triggerIconClassName,
  Icon,
  iconClassName,
  showSelectedOptionsChips = true,
  chipClassName,
  moreTagClassName,
  moreTagText,
  autoFitChips = false,
  addButtonIcon: AddButtonIcon,
  addButtonLabel,
  addButtonClassName = "",
  addButtonIconClassName = "",
  addButtonLabelClassName = "",
  addButtonLoading = false,
  addButtonLoadingText = "Adding...",
  addButtonLoader,
  onAddButtonClick,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [apiOptions, setApiOptions] = useState<MultiSelectDropdownOption[]>([]);
  const [isAddingItem, setIsAddingItem] = useState(false);

  const isAddLoading = addButtonLoading || isAddingItem;

  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout>();
  const [portalPosition, setPortalPosition] = useState<{
    top?: number;
    bottom?: number;
    left: number;
    width: number;
  }>({ left: 0, width: 0 });

  const dropdownId =
    id || `multiselect-dropdown-${Math.random().toString(36).slice(2, 9)}`;
  const listboxId = `${dropdownId}-listbox`;
  const triggerId = `${dropdownId}-trigger`;
  const defaultAriaDescribedBy = `${dropdownId}-description`;

  const effectiveOptions = useMemo(() => {
    if (enableApiSearch) {
      if (searchQuery.trim()) {
        return apiOptions || [];
      } else {
        const selectedApiOptions = (apiOptions || [])?.filter((option) =>
          value.includes(option.value)
        );
        return [...(initialOptions || []), ...selectedApiOptions];
      }
    }
    return options || [];
  }, [
    enableApiSearch,
    searchQuery,
    apiOptions,
    initialOptions,
    options,
    value,
  ]);

  const filteredAndSortedOptions = useMemo(() => {
    if (enableApiSearch) {
      const selected = effectiveOptions.filter((option) =>
        value.includes(option.value)
      );
      const unselected = effectiveOptions.filter(
        (option) => !value.includes(option.value)
      );
      return [...selected, ...unselected];
    }

    const filtered = showSearch
      ? effectiveOptions.filter((option) => {
          const searchText = option.label || option.value;
          return searchText?.toLowerCase()?.includes(searchQuery.toLowerCase());
        })
      : effectiveOptions;

    const selected = filtered.filter((option) => value.includes(option.value));
    const unselected = filtered.filter(
      (option) => !value.includes(option.value)
    );

    return [...selected, ...unselected];
    //value is not in dependencies to prevent re-ordering of options when we select an option
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveOptions, searchQuery, showSearch, enableApiSearch, isOpen]);

  const selectedOptions = useMemo(() => {
    const allOptions = enableApiSearch
      ? [...(options || []), ...(initialOptions || []), ...(apiOptions || [])]
      : options || [];

    return allOptions.filter((option) => value.includes(option.value));
  }, [enableApiSearch, options, initialOptions, apiOptions, value]);

  const handleApiSearch = useCallback(
    async (query: string) => {
      if (!enableApiSearch || !onSearch || !query.trim()) {
        setApiOptions([]);
        return;
      }

      setIsSearching(true);
      try {
        const results = await onSearch(query);
        setApiOptions(results);
      } catch (error) {
        console.error("API search failed:", error);
        setApiOptions([]);
      } finally {
        setIsSearching(false);
      }
    },
    [enableApiSearch, onSearch]
  );

  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (enableApiSearch && searchQuery.trim()) {
      setIsTyping(true);
      searchTimeoutRef.current = setTimeout(() => {
        setIsTyping(false);
        handleApiSearch(searchQuery);
      }, searchDebounceMs);
    } else if (enableApiSearch && !searchQuery.trim()) {
      setIsTyping(false);
    }

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery, enableApiSearch, handleApiSearch, searchDebounceMs]);

  const updatePortalPosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const dropdownMaxHeight = 300;
    const gap = 4;

    const spaceBelow = window.innerHeight - rect.bottom - gap;
    const spaceAbove = rect.top - gap;
    const openAbove = spaceBelow < dropdownMaxHeight && spaceAbove > spaceBelow;

    setPortalPosition(
      openAbove
        ? {
            bottom: window.innerHeight - rect.top + gap,
            left: rect.left,
            width: rect.width,
          }
        : { top: rect.bottom + gap, left: rect.left, width: rect.width }
    );
  }, []);

  const handleToggle = useCallback(() => {
    if (!disabled) {
      if (!isOpen) updatePortalPosition();
      setIsOpen((prev) => !prev);
      setFocusedIndex(-1);
      setSearchQuery("");
      if (!isOpen && showSearch) {
        setTimeout(() => searchInputRef.current?.focus(), 100);
      }
    }
  }, [disabled, isOpen, showSearch, updatePortalPosition]);

  const handleOptionToggle = useCallback(
    (option: MultiSelectDropdownOption) => {
      if (!option.disabled) {
        const newValues = value.includes(option.value)
          ? value.filter((v) => v !== option.value)
          : [...value, option.value];

        const newSelectedOptions = effectiveOptions.filter((opt) =>
          newValues.includes(opt.value)
        );
        onChange(newValues, newSelectedOptions);
        requestAnimationFrame(updatePortalPosition);
      }
    },
    [value, onChange, effectiveOptions, updatePortalPosition]
  );

  const handleAddButtonClick = useCallback(async () => {
    if (!onAddButtonClick || !searchQuery?.trim() || isAddLoading) return;
    setIsAddingItem(true);
    try {
      await onAddButtonClick(searchQuery);
    } finally {
      setIsAddingItem(false);
      setSearchQuery("");
    }
  }, [onAddButtonClick, searchQuery, isAddLoading]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (disabled) return;

      switch (event.key) {
        case "Enter": {
          event.preventDefault();
          if (isOpen && focusedIndex >= 0) {
            handleOptionToggle(filteredAndSortedOptions[focusedIndex]);
          } else if (
            isOpen &&
            onAddButtonClick &&
            searchQuery?.trim()?.length > 0 &&
            !isAddLoading &&
            !effectiveOptions?.some(
              (opt) =>
                (opt?.label || opt?.value)?.toLowerCase() ===
                searchQuery?.trim()?.toLowerCase()
            )
          ) {
            handleAddButtonClick();
          } else {
            handleToggle();
          }
          break;
        }
        case " ":
          event.preventDefault();
          if (isOpen && focusedIndex >= 0) {
            handleOptionToggle(filteredAndSortedOptions[focusedIndex]);
          } else {
            handleToggle();
          }
          break;
        case "Escape":
          if (isOpen) {
            setIsOpen(false);
            setFocusedIndex(-1);
            setSearchQuery("");
            triggerRef.current?.focus();
          }
          break;
        case "ArrowDown":
          event.preventDefault();
          if (!isOpen) {
            updatePortalPosition();
            setIsOpen(true);
            setFocusedIndex(0);
          } else {
            setFocusedIndex((prev) => {
              const nextIndex =
                prev < filteredAndSortedOptions.length - 1 ? prev + 1 : 0;
              for (
                let i = nextIndex;
                i < filteredAndSortedOptions.length;
                i++
              ) {
                if (!filteredAndSortedOptions[i].disabled) return i;
              }
              for (let i = 0; i < nextIndex; i++) {
                if (!filteredAndSortedOptions[i].disabled) return i;
              }
              return prev;
            });
          }
          break;
        case "ArrowUp":
          event.preventDefault();
          if (isOpen) {
            setFocusedIndex((prev) => {
              const nextIndex =
                prev > 0 ? prev - 1 : filteredAndSortedOptions.length - 1;
              for (let i = nextIndex; i >= 0; i--) {
                if (!filteredAndSortedOptions[i].disabled) return i;
              }
              for (
                let i = filteredAndSortedOptions.length - 1;
                i > nextIndex;
                i--
              ) {
                if (!filteredAndSortedOptions[i].disabled) return i;
              }
              return prev;
            });
          }
          break;
        case "Home":
          event.preventDefault();
          if (isOpen) {
            const firstEnabledIndex = filteredAndSortedOptions.findIndex(
              (option) => !option.disabled
            );
            if (firstEnabledIndex !== -1) {
              setFocusedIndex(firstEnabledIndex);
            }
          }
          break;
        case "End":
          event.preventDefault();
          if (isOpen) {
            for (let i = filteredAndSortedOptions.length - 1; i >= 0; i--) {
              if (!filteredAndSortedOptions[i].disabled) {
                setFocusedIndex(i);
                break;
              }
            }
          }
          break;
      }
    },
    [
      disabled,
      isOpen,
      focusedIndex,
      filteredAndSortedOptions,
      handleToggle,
      handleOptionToggle,
      updatePortalPosition,
      handleAddButtonClick,
      onAddButtonClick,
      isAddLoading,
      searchQuery,
      effectiveOptions,
    ]
  );

  const handleOverlayClick = useCallback(() => {
    setIsOpen(false);
    setFocusedIndex(-1);
    setSearchQuery("");
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [isOpen]);

  return (
    <div className={`flex flex-col gap-2 ${className}`} ref={containerRef}>
      {label && (
        <label
          htmlFor={triggerId}
          className={`${
            labelClassName ||
            "font-medium text-heading-text dark:text-heading-text-dk"
          }`}
        >
          {label}
        </label>
      )}

      <div
        ref={triggerRef}
        id={triggerId}
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-controls={listboxId}
        aria-label={ariaLabel || "Select multiple options"}
        aria-describedby={ariaDescribedBy || defaultAriaDescribedBy}
        tabIndex={disabled ? -1 : 0}
        className={`select-none cursor-pointer rounded-[6px] bg-popper-bg dark:bg-popper-bg-dk border border-border-gray dark:border-border-gray-dk backdrop-blur-[10px] transition-colors focus:outline-none flex items-center
          ${disabled && "opacity-80 "}
          ${triggerClassName}
        `}
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
      >
        <div
          className={`flex items-center gap-[4px] justify-between w-full px-[8px] py-[6px] ${inputContainerClassName}`}
        >
          {Icon && <Icon className={iconClassName} />}
          {selectedOptions?.length > 0 && showSelectedOptionsChips ? (
            <SelectedChips
              selectedOptions={selectedOptions}
              maxDisplayedSelections={maxDisplayedSelections}
              onOptionToggle={handleOptionToggle}
              containerRef={containerRef}
              chipClassName={chipClassName}
              moreTagClassName={moreTagClassName}
              moreTagText={moreTagText}
              autoFitChips={autoFitChips}
            />
          ) : (
            <span
              className={`overflow-hidden text-ellipsis whitespace-nowrap flex-1 min-w-0 pr-1 ${
                placeholderClassName ||
                "text-sm font-medium text-heading-text dark:text-heading-text-dk"
              }`}
            >
              {placeholder}
            </span>
          )}
          {showChevron && (
            <ChevronDownIcon
              className={`w-[8px] h-[8px] shrink-0 text-heading-text dark:text-heading-text-dk transition-transform duration-300 ${
                isOpen ? "rotate-180" : ""
              } ${triggerIconClassName}`}
            />
          )}
        </div>
      </div>

      {isOpen &&
        createPortal(
          <>
            <div
              className="fixed inset-0 overflow-hidden"
              style={{ zIndex: 9999998 }}
              onClick={handleOverlayClick}
              onWheel={(e) => e.preventDefault()}
              onTouchMove={(e) => e.preventDefault()}
            />
            <div
              ref={dropdownRef}
              id={listboxId}
              role="listbox"
              aria-label="Options"
              aria-activedescendant={
                focusedIndex >= 0
                  ? `${dropdownId}-option-${focusedIndex}`
                  : undefined
              }
              aria-multiselectable="true"
              className={`
                fixed overflow-y-auto ${visibleScrollbar(
                  "[&::-webkit-scrollbar]:w-[8px]"
                )} backdrop-blur-[10px]
                bg-pure-white dark:bg-pure-white-dk border border-border-gray dark:border-border-gray-dk
                rounded-[8px] shadow-popper-blur-effect max-h-[300px]
                ${dropdownOpenClassName || ""}
                ${dropdownClassName}
              `}
              style={{
                ...(portalPosition.bottom !== undefined
                  ? { bottom: portalPosition.bottom }
                  : { top: portalPosition.top }),
                left: portalPosition.left,
                width: portalPosition.width,
                zIndex: 9999999,
              }}
            >
              <SearchInput
                searchQuery={searchQuery}
                searchPlaceholder={searchPlaceholder}
                searchInputRef={searchInputRef}
                onSearchQueryChange={(query) => {
                  setSearchQuery(query);
                  setFocusedIndex(-1);
                }}
                showSearch={showSearch}
                onKeyDown={handleKeyDown}
                className={searchInputClassName}
                searchIconClassName={searchIconClassName}
              />

              {onAddButtonClick &&
                searchQuery?.trim()?.length > 0 &&
                !effectiveOptions?.some(
                  (opt) =>
                    (opt?.label || opt?.value)?.toLowerCase() ===
                    searchQuery?.trim()?.toLowerCase()
                ) && (
                  <button
                    type="button"
                    disabled={isAddLoading}
                    className={`flex items-center gap-1 w-max px-3 py-[10px] bg-transparent border-none outline-none ${isAddLoading ? "opacity-60 cursor-not-allowed" : "cursor-pointer"} ${addButtonClassName}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAddButtonClick();
                    }}
                  >
                    {isAddLoading ? (
                      <>
                        {addButtonLoader ?? (
                          <CircularLoader
                            size={14}
                            thickness={2}
                            className={
                              addButtonIconClassName ||
                              "text-verbablue dark:text-verbablue-dk"
                            }
                          />
                        )}
                        <span
                          className={
                            addButtonLabelClassName ||
                            "text-xs font-semibold text-verbablue dark:text-verbablue-dk"
                          }
                        >
                          {addButtonLoadingText}
                        </span>
                      </>
                    ) : (
                      <>
                        {AddButtonIcon && (
                          <AddButtonIcon
                            className={
                              addButtonIconClassName ||
                              "w-[14px] h-[14px] text-verbablue dark:text-verbablue-dk"
                            }
                          />
                        )}
                        {addButtonLabel && (
                          <span
                            className={
                              addButtonLabelClassName ||
                              "text-xs font-semibold text-verbablue dark:text-verbablue-dk"
                            }
                          >
                            {typeof addButtonLabel === "function"
                              ? addButtonLabel(searchQuery)
                              : addButtonLabel}
                          </span>
                        )}
                      </>
                    )}
                  </button>
                )}

              <OptionsList
                filteredAndSortedOptions={filteredAndSortedOptions}
                value={value}
                focusedIndex={focusedIndex}
                dropdownId={dropdownId}
                optionClassName={optionClassName}
                searchQuery={searchQuery}
                onOptionToggle={handleOptionToggle}
                onFocusedIndexChange={setFocusedIndex}
                isSearching={isTyping || isSearching}
                loadingText={loadingText}
                noResultsText={noResultsText}
                enableApiSearch={enableApiSearch}
                className={optionsListClassName}
                selectedOptionClassName={selectedOptionClassName}
              />
            </div>
          </>,
          document.body
        )}
    </div>
  );
};

export default MultiSelectSearchableDropdown;
